import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  RagQueryRequest,
  RagQueryResponse,
  RagSearchRequest,
  RagSearchResult
} from '../models/rag.models';

/**
 * RagApiClient — Single HTTP gateway to the RAG proxy exposed by the security service.
 *
 * Responsibilities:
 *  • Build URLs from `${environment.backendUrl}/rag`
 *  • Execute POST /chat/query and POST /chat/search
 *  • Map HTTP errors (including proxy-specific partner errors) to user-friendly messages
 *
 * Does NOT manage UI state or conversation history.
 * Does NOT add auth/tenant headers — that's `RagAuthInterceptor`'s job.
 */
@Injectable({ providedIn: 'root' })
export class RagApiClient {

  private readonly baseUrl = `${environment.backendUrl}/rag`;

  constructor(private http: HttpClient) {}

  /**
   * Send a question through the full RAG pipeline (retrieval + LLM).
   *
   * @param request - Query request with question and conversationId (null for new)
   * @returns Observable with response including conversationId
   */
  query(request: RagQueryRequest): Observable<RagQueryResponse> {
    return this.http
      .post<RagQueryResponse>(`${this.baseUrl}/chat/query`, request, {
        headers: this.buildHeaders()
      })
      .pipe(catchError(err => this.handleError(err)));
  }

  /**
   * Semantic search only (no LLM generation).
   */
  search(request: RagSearchRequest): Observable<RagSearchResult[]> {
    return this.http
      .post<RagSearchResult[]>(`${this.baseUrl}/chat/search`, request, {
        headers: this.buildHeaders()
      })
      .pipe(catchError(err => this.handleError(err)));
  }

  // ─── Private helpers ──────────────────────────────────────────────

  /**
   * Mark requests so the RagAuthInterceptor can identify them.
   * The actual Authorization + X-Partner-ID headers are added by the interceptor.
   */
  private buildHeaders(): HttpHeaders {
    return new HttpHeaders({ 'Content-Type': 'application/json' });
  }

  /**
   * Translate HTTP status codes (and proxy error codes) into UX-friendly error messages.
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    const code = this.errorCode(error);
    let message: string;

    switch (error.status) {
      case 400:
        message = code === 'missing_partner_header' || code === 'invalid_partner_header'
          ? 'Selecciona un portal para usar el chat con datos de tu cuenta.'
          : 'Solicitud inválida. Intenta de nuevo.';
        break;
      case 403:
        message = 'No tienes acceso a los datos de ese portal.';
        break;
      case 429:
        message = 'Estás haciendo muchas consultas. Intenta en unos segundos.';
        break;
      case 502:
      case 503:
      case 504:
        message = 'El chat no está disponible en este momento. Reintenta en unos minutos.';
        break;
      case 401:
        message = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.';
        break;
      case 0:
        message = 'No se pudo conectar con el servicio de chat. Verifica tu conexión.';
        break;
      default:
        message = error.error?.message
          ?? `Error inesperado (${error.status}). Intenta de nuevo.`;
    }

    return throwError(() => new Error(message));
  }

  private errorCode(error: HttpErrorResponse): string | undefined {
    return typeof error.error === 'string' ? error.error : error.error?.error;
  }
}
